import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Star, Clock, MapPin, Users } from 'lucide-react';
import { formatPrice, getCategoryColor, getCategoryLabel, truncateText } from '../../lib/utils';
import { useSettings } from '../../contexts/SettingsContext';
import { PopularityBadge, SpotsLeftBadge } from './UrgencyBadges';

export const TourCard = ({ tour }) => {
  const { settings } = useSettings();
  
  // Check if all badges should be hidden
  const hideAllBadges = tour.hide_all_badges === true;
  
  return (
    <Link to={`/tours/${tour.id}`} data-testid={`tour-card-${tour.id}`}>
      <Card className="tour-card group overflow-hidden bg-white border border-border/50 rounded-2xl h-full">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={tour.image_url}
            alt={tour.title}
            className="tour-card-image w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Category Badge - Hide if all badges hidden or category badge disabled */}
          {!hideAllBadges && tour.show_category_badge !== false && (
            <Badge 
              className={`absolute top-4 left-4 ${getCategoryColor(tour.category)} border-0`}
              data-testid={`tour-category-${tour.id}`}
            >
              {tour.category_badge_text || getCategoryLabel(tour.category)}
            </Badge>
          )}

          {/* Popularity Badge - Hide if all badges hidden */}
          {!hideAllBadges && (
            <div className="absolute top-4 right-4">
              <PopularityBadge tour={tour} />
            </div>
          )}
          
          {/* Price Badge */}
          <div className="absolute bottom-4 right-4 price-badge px-3 py-1 rounded-full font-bold text-lg" data-testid={`tour-price-${tour.id}`}>
            {formatPrice(tour.price)}
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Title */}
          <h3 className="font-heading text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors" data-testid={`tour-title-${tour.id}`}>
            {tour.title}
          </h3>

          {/* Spots Left Badge */}
          <div className="mb-2">
            <SpotsLeftBadge tour={tour} />
          </div>

          {/* Description */}
          <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
            {truncateText(tour.description, 100)}
          </p>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{tour.duration}</span>
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              <span>{tour.location.split(',')[0]}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>Max {tour.max_guests}</span>
            </div>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-secondary text-secondary" />
              <span className="font-semibold text-foreground">{tour.rating}</span>
            </div>
            <span className="text-muted-foreground text-sm">
              ({tour.reviews_count} reviews)
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
};
